import * as React from 'react';
import '@styles/app.css';
import { switchAccount } from '../auth/msal';
import { http } from '../api/http';
import { FaSignOutAlt, FaSearchPlus } from 'react-icons/fa';
import logoUrl from '../Images/scansys_logo.png';

export interface MenuProps {
  selectedDrawerName: string | null;
  onDrawerSelect: (id: number, name: string) => void;
  onRouteChange?: (route: string) => void;
  currentRoute?: string | null;
  userName?: string | null;
  userId?: string | null;
  isAdmin?: boolean; // new prop
  drawerPages?: string[]; // ordered page keys
}

const STATIC_MENU_ITEMS = [
  { key: 'drawers', label: 'Classes' },
  {
    key: 'admin',
    label: 'Admin',
    submenu: [
      { key: 'users', label: 'Users' },
      { key: 'groups', label: 'Groups' },
      { key: 'fielddefinitions', label: 'Field Definitions' },
      { key: 'drawers-admin', label: 'Drawers' },
      { key: 'documenttypes', label: 'Document Types' }
    ]
  }
];

const PAGE_LABELS: Record<string, string> = {
  browse: 'Browse',
  search: 'Search',
  dossier: 'Dossier'
};

const DEFAULT_PAGES = ['browse', 'search', 'dossier'];

const ZOOM_KEY = 'uiZoomPercent';
const ZOOM_OPTIONS = [70,75,80,85,90,95,100];
const DEFAULT_ZOOM = 85;

export const Menu = ({ selectedDrawerName, onDrawerSelect, onRouteChange, currentRoute, userName, isAdmin, drawerPages }: MenuProps) => {
  const [adminOpen, setAdminOpen] = React.useState(false);

  // Zoom dropdown state
  const [zoomOpen, setZoomOpen] = React.useState(false);
  const [zoomPercent, setZoomPercent] = React.useState<number>(DEFAULT_ZOOM);

  // Close zoom dropdown when clicking outside
  const zoomRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    function handleDocClick(e: MouseEvent) {
      if (zoomOpen && zoomRef.current && !zoomRef.current.contains(e.target as Node)) {
        setZoomOpen(false);
      }
    }
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [zoomOpen]);

  const handleSwitchAccount = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    switchAccount();
  }, []);

  const handleDrawerSelect = React.useCallback((id: number, name: string) => {
    onDrawerSelect(id, name);
  }, [onDrawerSelect]);

  function routeForSubKey(subKey: string): string | null {
    switch (subKey) {
      case 'users': return 'admin-users';
      case 'groups': return 'admin-groups';
      case 'drawers-admin': return 'admin-drawers-admin';
      case 'fielddefinitions': return 'admin-fielddefinitions';
      case 'documenttypes': return 'admin-documenttypes';
      default: return null;
    }
  }

  const effectivePages = React.useMemo(() => {
    const raw = (drawerPages && drawerPages.length) ? drawerPages : DEFAULT_PAGES;
    return raw.filter(p => PAGE_LABELS[p]);
  }, [drawerPages]);

  const visibleMenuItems = React.useMemo(() => {
    let items = isAdmin ? STATIC_MENU_ITEMS : STATIC_MENU_ITEMS.filter(m => m.key !== 'admin');

    // Insert configured pages after "Classes"
    const pages = effectivePages.map(p => ({ key: p, label: PAGE_LABELS[p] }));
    items = [{ key: 'drawers', label: 'Classes' }, ...pages, ...items.filter(i => i.key !== 'drawers')];

    // Hide pages when on the Classes page.
    if (currentRoute === 'drawers') {
      items = items.filter(m => m.key === 'drawers' || m.key === 'admin');
    }
    return items;
  }, [isAdmin, currentRoute, effectivePages]);

  // Zoom handling: load stored value and apply CSS variable
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(ZOOM_KEY);
      const val = stored ? Number(stored) : DEFAULT_ZOOM;
      const normalized = ZOOM_OPTIONS.includes(val) ? val : DEFAULT_ZOOM;
      setZoomPercent(normalized);
      document.documentElement.style.setProperty('--ui-scale', String(normalized / 100));
    } catch (e) {
      // ignore
    }
  }, []);

  const applyZoom = React.useCallback((percent: number) => {
    setZoomPercent(percent);
    try {
      localStorage.setItem(ZOOM_KEY, String(percent));
      document.documentElement.style.setProperty('--ui-scale', String(percent / 100));
    } catch (e) {
      // ignore
    }
    setZoomOpen(false);
  }, []);

  return (
    <header className="app-header">
      <div className="brand">
              {selectedDrawerName && <span className="drawer-badge">{selectedDrawerName}</span>}

      </div>
      <nav className="main-nav">
        {visibleMenuItems.map(item => {
          if (item.key === 'drawers' || item.key === 'search' || item.key === 'browse' || item.key === 'dossier') {
            return (
              <button
                key={item.key}
                type="button"
                className="nav-link nav-link-clickable"
                onClick={() => { if (onRouteChange) onRouteChange(item.key); }}
              >
                {item.label}
              </button>
            );
          }
          if (item.submenu) {
            return (
              <div
                key={item.key}
                className={`nav-item dropdown ${adminOpen ? 'open' : ''}`}
                tabIndex={0}
                onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setAdminOpen(false); }}
              >
                <button
                  type="button"
                  className="nav-link nav-link-button"
                  aria-haspopup="true"
                  aria-expanded={adminOpen}
                  onClick={() => setAdminOpen(o => !o)}
                >
                  {item.label}
                </button>
                {adminOpen && (
                  <div className="dropdown-menu">
                    {item.submenu.map(sub => (
                      <button
                        key={sub.key}
                        type="button"
                        className="dropdown-item"
                        onClick={() => { if (onRouteChange) { const r = routeForSubKey(sub.key); if (r) onRouteChange(r); } setAdminOpen(false); }}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <span key={item.key} className="nav-link nav-link-static">{item.label}</span>
          );
        })}
      </nav>
      <div className="drawer-badge-wrapper">
        {/* Zoom control */}
        <div ref={zoomRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="badge-button"
            onClick={() => setZoomOpen(o => !o)}
            title="Zoom"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <FaSearchPlus /> {zoomPercent}%
          </button>
          {zoomOpen && (
            <div className="dropdown-menu" style={{ right: 0, left: 'auto' }}>
              {ZOOM_OPTIONS.map(p => (
                <button key={p} type="button" className="dropdown-item" onClick={() => applyZoom(p)}>
                  {p}%
                </button>
              ))}
            </div>
          )}
        </div>

        {userName && (
          <button
            type="button"
            className="badge-button login-name"
            onClick={handleSwitchAccount}
            title="Switch account"
          >
            <FaSignOutAlt /> {userName}
          </button>
        )}
        {/* Scansys logo on the right */}
              <img className="scansys-logo" src={logoUrl} alt="Scansys" />
      </div>
    </header>
  );
}

export default Menu;
